import i18n from "i18next";
import { isArray, isEmpty, isObject } from "lodash";

function serializeFilterValue(value) {
  if (Array.isArray(value)) {
    const values = value.filter((item) => item != null && item !== "");
    return `[${values.map((item) => String(item)).join(",")}]`;
  }

  return `'${value}'`;
}

function serializeFilterNode(node) {
  const unary = ["ISNULL", "ISNOTNULL", "ISFALSE", "ISTRUE"];

  if (Array.isArray(node)) {
    return node.map(serializeFilterNode).join(" AND ");
  }

  if (node?.logical && Array.isArray(node.fields)) {
    const expression = node.fields
      .map(serializeFilterNode)
      .join(` ${node.logical} `);
    return `(${expression})`;
  }

  if (node?.operator && unary.includes(node.operator)) {
    return `${node.name}:${node.operator}`;
  }

  return `${node.name}:${node.operator}:${serializeFilterValue(node.value)}`;
}

export function constructFilter(filter) {
  if (!filter) {
    return "";
  }
  if (isEmpty(filter)) {
    return "";
  }
  if (typeof filter?.toJSON === "function") {
    return `filter=${encodeURIComponent(serializeFilterNode(filter.toJSON()))}`;
  }
  if (typeof filter?.toQueryString === "function") {
    return `filter=${filter.toQueryString()}`;
  }
  if (!isArray(filter)) {
    if (filter.startsWith("filter=")) {
      return filter;
    }
    return "filter=" + filter;
  }

  filter = filter
    .map((filterObject) => {
      if (!validFilterObject(filterObject)) {
        console.warn(
          "Invalid filter object in filter array!",
          JSON.stringify(filterObject)
        );
      }
      return (
        filterObject.name +
        ":" +
        filterObject.operator +
        (filterObject.value ? ":" + filterObject.value : "")
      );
    })
    .join(" AND ");

  return "filter=" + encodeURI(filter);
}

export function destructFilter(filterString) {
  if (isEmpty(filterString)) {
    return [];
  }

  filterString = filterString.replace(/^filter=/, "");
  filterString = decodeURI(filterString);
  const filterParts = filterString.split(" AND ");

  return filterParts.map((part) => {
    const [name, operator, ...value] = part.split(":");
    return { name: name, operator: operator, value: value.join(":") };
  });
}

function validFilterObject(filter) {
  if (isEmpty(filter)) {
    return false;
  }

  const keysNumber = Object.keys(filter).length;
  if (keysNumber < 2 || keysNumber > 3) {
    return false;
  }

  if (!filter.hasOwnProperty("name") || !filter.hasOwnProperty("operator")) {
    return false;
  }

  if (isEmpty(filter.name)) return false;
  return !isEmpty(filter.operator);
}

export function constructSort(sort, defaultSort) {
  if (sort && typeof sort?.toQueryString === "function") {
    const qs = sort.toQueryString({ includeAsc: true }); // ensure asc explicitly if you want
    return qs || "";
  }

  if (sort && Array.isArray(sort?.orders)) {
    const parts = sort.orders
      .map((o) => {
        const name = o?.name ?? o?.property;
        const dir = (o?.direction ?? "").toLowerCase();
        if (!name || (dir !== "asc" && dir !== "desc")) return null;
        // keep comma unencoded; encode property only (like your ApiSort does)
        return `sort=${encodeURIComponent(name)},${dir}`;
      })
      .filter(Boolean)
      .join("&");
    return parts;
  }

  const sorts = [];
  // console.log("constructSort called with:", sort, defaultSort);

  if (sort && isArray(sort)) {
    sort.forEach((s) =>
      sorts.push({ name: s.name ?? s.property, direction: s.direction })
    );
  } else if (sort && Object.keys(sort).length > 0) {
    const [key, direction] = Object.entries(sort)[0];
    sorts.push({ name: key, direction: direction });
  }

  if (defaultSort) {
    defaultSort = Array.isArray(defaultSort) ? defaultSort : [defaultSort];
    defaultSort.forEach((s) =>
      sorts.push({ name: s.name, direction: s.direction })
    );
  }

  return sorts
    .filter((s) => s.direction === "asc" || s.direction === "desc")
    .map((s) => `sort=${encodeURIComponent(`${s.name},${s.direction}`)}`)
    .join("&");
}

export function destructSort(sortString) {
  if (isEmpty(sortString)) {
    return {};
  }

  sortString = sortString.replace(/^sort=/, "");
  const sorts = sortString.split("&sort=");

  return sorts.reduce((result, sort) => {
    const [name, direction] = sort.split(",");
    result[name] = direction;
    return result;
  }, {});
}

export function addDefaultSort(sort, defaultSort) {
  return constructSort(destructSort(sort), defaultSort);
}

export function constructPage(page, pageSize) {
  if (isNaN(page) || isNaN(pageSize)) {
    return "";
  }

  return "page=" + page + "&size=" + pageSize;
}

export function destructPage(pageString) {
  if (isEmpty(pageString)) {
    return undefined;
  }

  if (
    Number.isInteger(pageString?.page) &&
    Number.isInteger(pageString?.size)
  ) {
    return { page: pageString.page, size: pageString.size };
  }

  pageString = pageString.replace(/^page=/, "");
  const [page, pageSize] = pageString.split("&size=");
  return { page: parseInt(page), size: parseInt(pageSize) };
}

export function paginateArray({ page, array }) {
  const pageSize = page?.pageSize ?? page?.size ?? 10;
  const pageObject = {
    number: page?.pageNumber ?? page?.page ?? 0,
    size: pageSize,
    totalElements: array?.length ?? 0,
    totalPages: Math.ceil(array?.length / pageSize),
  };

  const start = pageObject.number * pageObject.size;
  const end = start + pageObject.size;

  const pageContent = (array ?? []).slice(start, end);
  return {
    content: pageContent,
    pageable: pageObject,
    ...pageObject,
  };
}

export function constructParameters(page, filter, sort) {
  if (isObject(page)) {
    if (page && typeof page?.toQueryString === "function") {
      page = page.toQueryString(false);
    } else {
      page = constructPage(page.page, page.size);
    }
  }
  filter = constructFilter(filter);

  if (isObject(sort) || isArray(sort)) {
    sort = constructSort(sort);
  }
  return [page, filter, sort].filter((item) => !!item).join("&");
}

/**
 * Extracts error message from API response object OR any Exception object occurred inside a RTK thunk
 *
 * Returned string will most likely to be used as a "details" parameter
 * for the generic error notification, so it should be as informative as possible,
 * but also safe to display to end users (no raw stack traces or unprocessed error objects)
 *
 * See notification.generic.failedTo*DataExt translations for examples of how
 * the returned message is used in the UI.
 *
 * @param {object} error - The error object to extract the message from
 * @returns {string} The error message
 */
export function getErrorMessage(error) {
  const {
    // @var string
    message,
    // @var string
    httpStatus,

    // @var Array<{ code: number; text: string; level: string }>
    result,
    // @var string
    resultMessages,
  } = error?.response?.data || error?.response || error || {};

  // Go with the resultMessages if it's a non-empty string, otherwise try to extract from result array
  if (!!resultMessages) {
    // remove "UPPER_CASE: " prefix if present
    const pretty = resultMessages.replace(/^[A-Z_]+:\s*/, "");

    return pretty;
  }

  // If result is a non-empty array, extract the most severe error message
  if (result && Array.isArray(result) && result.length > 0) {
    const [{ text } = {}] = result
      .filter((msg) => msg.level === "ERROR")
      .sort((a, b) => Number(b.code) - Number(a.code));

    if (!!text) {
      return text;
    }
  }

  return message || httpStatus || i18n.t("unknownError");
}

/**
 * Checks if the error is a network error (i.e. the client is offline).
 *
 * @param {object} param0
 * @returns {boolean}
 */
export function isNetworkError({ code } = {}) {
  return code === "ERR_NETWORK";
}

/**
 * Helper function to catch offline errors and return specified values instead.
 *
 * @param {any} offlineValues
 * @returns any
 */
export function catchOffline(offlineValues) {
  return (error) =>
    isNetworkError(error) ? offlineValues : Promise.reject(error);
}
